import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarCheck, CalendarClock, CalendarDays, CalendarX2, Clock, Copy, CopyPlus, ExternalLink, Mail, MapPin, MoreVertical,
  Pencil, Phone, Plus, Search, Send, Trash2, Video, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { initials } from '../../lib/format';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  DIAS_CLAVE, capital, esUrl, fmtHora, hora12DeHhmm, hoyEn, mensajeError, sumarDias, textoDiaLargo, ymdDe,
} from '../../lib/calendario';
import EditorPagina, { paginaAForm } from './EditorPagina';

/*
 * «Citas» (rediseño 2026-10-08): resumen de lo que viene, las páginas de reserva con su horario y su próxima cita,
 * y las citas por día (próximas, pasadas y canceladas) con lo que se hace con ellas: verlas en el calendario,
 * escribir a quien reservó, unirse a la videollamada o cancelarlas con motivo (POST /calendar/citas/{id}/cancelar).
 */

const PESTANAS = [
  ['proximas', 'Próximas', { periodo: 'proximas', estado: 'confirmada' }],
  ['pasadas', 'Pasadas', { periodo: 'pasadas', estado: 'confirmada' }],
  ['canceladas', 'Canceladas', { periodo: 'todas', estado: 'cancelada' }],
];
const DIAS_CORTO = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const duracionTexto = (min) => (min < 60 ? `${min} min` : min % 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min / 60} h`);

/** «Lun–Vie · 9:00 a. m. – 5:00 p. m.» (días seguidos con el mismo horario se juntan). */
export function resumenHorario(horario) {
  const clave = (d) => JSON.stringify((horario || {})[d] || []);
  const grupos = [];
  DIAS_CLAVE.forEach((d, i) => {
    const tramos = (horario || {})[d] || [];
    if (!tramos.length) return;
    const ult = grupos[grupos.length - 1];
    if (ult && ult.fin === i - 1 && ult.clave === clave(d)) ult.fin = i;
    else grupos.push({ ini: i, fin: i, clave: clave(d), tramos });
  });
  if (!grupos.length) return 'Sin horario';
  return grupos.map((g) => {
    const dias = g.ini === g.fin ? DIAS_CORTO[g.ini] : `${DIAS_CORTO[g.ini]}${g.fin - g.ini > 1 ? '–' : ', '}${DIAS_CORTO[g.fin]}`;
    const horas = g.tramos.map(([a, b]) => `${hora12DeHhmm(a)} – ${hora12DeHhmm(b === '24:00' ? '23:59' : b)}`).join(', ');
    return `${dias} · ${horas}`;
  }).join(' · ');
}

/** «Hoy», «Mañana», «Ayer» o «Martes, 13 de octubre». */
function etiquetaDia(ymd, hoy) {
  if (ymd === hoy) return 'Hoy';
  if (ymd === sumarDias(hoy, 1)) return 'Mañana';
  if (ymd === sumarDias(hoy, -1)) return 'Ayer';
  return capital(textoDiaLargo(ymd));
}

export default function PanelCitas({ tz, calendarios, principal, preguntas, onAbrirEvento }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const openComposer = useStore((st) => st.openComposer);
  const [pestana, setPestana] = useState('proximas');
  const [filtroPagina, setFiltroPagina] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [editor, setEditor] = useState(null);       // {pagina|null, inicial}
  const [cancelando, setCancelando] = useState(null); // la cita que se va a cancelar

  const { data: paginas, isLoading } = useQuery({
    queryKey: ['cal-paginas'],
    queryFn: async () => (await api.get('/calendar/paginas')).data.paginas || [],
    refetchInterval: 60000,
  });
  // Las próximas se piden siempre: dan el resumen de arriba aunque se esté viendo otra pestaña
  const { data: proximas } = useQuery({
    queryKey: ['cal-citas', 'proximas'],
    queryFn: async () => (await api.get('/calendar/citas', { params: PESTANAS[0][2] })).data.citas || [],
    refetchInterval: 60000,
  });
  const { data: otras } = useQuery({
    queryKey: ['cal-citas', pestana],
    queryFn: async () => (await api.get('/calendar/citas', { params: PESTANAS.find(([k]) => k === pestana)[2] })).data.citas || [],
    enabled: pestana !== 'proximas',
  });
  // Un backend anterior ignora `estado`: aquí se filtra igual para no mezclar canceladas con confirmadas
  const lista = useMemo(() => {
    const base = (pestana === 'proximas' ? proximas : otras) || null;
    if (!base) return null;
    return base.filter((c) => (pestana === 'canceladas' ? c.estado === 'cancelada' : c.estado === 'confirmada'));
  }, [pestana, proximas, otras]);

  const editables = calendarios.filter((c) => c.mi_rol === 'editor' || c.mi_rol === 'admin');
  const propios = calendarios.filter((c) => c.es_mio);
  const porPagina = useMemo(() => Object.fromEntries((paginas || []).map((p) => [p.id, p])), [paginas]);
  const hoy = hoyEn(tz);

  /* ---------- resumen ---------- */
  const resumen = useMemo(() => {
    const conf = (proximas || []).filter((c) => c.estado === 'confirmada');
    const dia = (c) => ymdDe(c.inicio, tz);
    return {
      hoy: conf.filter((c) => dia(c) === hoy).length,
      semana: conf.filter((c) => dia(c) < sumarDias(hoy, 7)).length,
      mes: conf.filter((c) => dia(c) < sumarDias(hoy, 30)).length,
      siguiente: conf.find((c) => new Date(c.fin) > new Date()),
    };
  }, [proximas, tz, hoy]);
  const activas = (paginas || []).filter((p) => p.activa).length;

  /* ---------- lista filtrada y agrupada por día ---------- */
  const grupos = useMemo(() => {
    if (!lista) return null;
    const q = busqueda.trim().toLowerCase();
    const filtradas = lista.filter((c) => (!filtroPagina || c.pagina_id === filtroPagina)
      && (!q || [c.nombre, c.correo, c.telefono].some((x) => (x || '').toLowerCase().includes(q))));
    const salida = [];
    for (const c of filtradas) {
      const d = ymdDe(c.inicio, tz);
      const ult = salida[salida.length - 1];
      if (ult && ult.dia === d) ult.citas.push(c);
      else salida.push({ dia: d, citas: [c] });
    }
    return salida;
  }, [lista, filtroPagina, busqueda, tz]);

  /* ---------- páginas ---------- */
  const abrirEditor = (pagina, extra = {}) => setEditor({
    pagina, inicial: { ...paginaAForm(pagina, { zona: tz, calendarioId: principal?.id }), ...extra },
  });
  const duplicar = (p) => abrirEditor(null, { titulo: `${p.titulo} (copia)`, slug: '', activa: true, calendario_id: p.calendario_id });

  const guardar = async (form) => {
    try {
      if (editor.pagina) await api.patch(`/calendar/paginas/${editor.pagina.id}`, form);
      else await api.post('/calendar/paginas', form);
    } catch (e) {
      throw new Error(mensajeError(e, 'No se pudo guardar la página'));
    }
    toast.success(editor.pagina ? 'Página guardada' : 'Página de reserva creada');
    setEditor(null);
    qc.invalidateQueries({ queryKey: ['cal-paginas'] });
  };

  const alternar = async (p) => {
    qc.setQueryData(['cal-paginas'], (l) => (l || []).map((x) => (x.id === p.id ? { ...x, activa: !p.activa } : x)));
    try {
      await api.patch(`/calendar/paginas/${p.id}`, { activa: !p.activa });
      toast.success(p.activa ? 'Página pausada: ya no admite reservas' : 'Página activa: ya se puede reservar');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo cambiar'));
    }
    qc.invalidateQueries({ queryKey: ['cal-paginas'] });
  };

  const borrar = async (p) => {
    const ok = await preguntas.confirmar({
      titulo: `¿Eliminar «${p.titulo}»?`,
      mensaje: 'El enlace dejará de funcionar. Las citas ya reservadas se quedan en tu calendario.',
    });
    if (!ok) return;
    try {
      await api.delete(`/calendar/paginas/${p.id}`);
      qc.invalidateQueries({ queryKey: ['cal-paginas'] });
      toast.success('Página eliminada');
    } catch (e) {
      toast.error(mensajeError(e, 'No se pudo eliminar'));
    }
  };

  const copiar = async (texto, aviso = 'Enlace copiado') => {
    try { await navigator.clipboard.writeText(texto); toast.success(aviso); } catch { toast.error('No se pudo copiar'); }
  };

  const escribir = (datos) => { openComposer('new', datos); nav('/mail'); };
  const enviarEnlace = (p) => escribir({
    subject: `Reserva una cita: ${p.titulo}`,
    html: `<p>Hola:</p><p>Puedes elegir el día y la hora que mejor te queden aquí:</p><p><a href="${p.url}">${p.url}</a></p>`,
  });

  /* ---------- citas ---------- */
  const cancelar = async ({ motivo, avisar }) => {
    const c = cancelando;
    try {
      await api.post(`/calendar/citas/${c.id}/cancelar`, { motivo: motivo || undefined, avisar });
    } catch (e) {
      throw new Error(mensajeError(e, 'No se pudo cancelar la cita'));
    }
    setCancelando(null);
    toast.success(avisar ? `Cita cancelada. Le avisamos a ${c.nombre} por correo.` : 'Cita cancelada');
    qc.invalidateQueries({ queryKey: ['cal-citas'] });
    qc.invalidateQueries({ queryKey: ['cal-paginas'] });
    qc.invalidateQueries({ queryKey: ['cal-eventos'] });
  };

  const hayPaginas = (paginas || []).length > 0;

  return (
    <div className="cal-citas od-scroll" data-testid="cal-citas">
      {hayPaginas && (
        <div className="cal-citas-resumen" data-testid="cal-citas-resumen">
          <Dato icono={<CalendarCheck />} valor={resumen.hoy} texto={resumen.hoy === 1 ? 'cita hoy' : 'citas hoy'} />
          <Dato icono={<CalendarDays />} valor={resumen.semana} texto="próximos 7 días" />
          <Dato icono={<CalendarClock />} valor={resumen.mes} texto="próximos 30 días" />
          <Dato
            icono={<Clock />}
            valor={resumen.siguiente ? fmtHora(resumen.siguiente.inicio, tz) : '—'}
            texto={resumen.siguiente ? `${etiquetaDia(ymdDe(resumen.siguiente.inicio, tz), hoy)} · ${resumen.siguiente.nombre}` : 'Sin citas próximas'}
            chico
          />
        </div>
      )}

      <div className="cal-citas-cab">
        <div className="min-w-0">
          <h3>Páginas de reserva {hayPaginas && <span className="cal-contador">{activas} de {paginas.length} activas</span>}</h3>
          <p className="cal-ayuda" style={{ fontSize: 13 }}>Comparte un enlace y que te reserven en tus horas libres, sin cuenta.</p>
        </div>
        <button type="button" className="od-btn-primary" onClick={() => abrirEditor(null)} data-testid="cal-pagina-nueva">
          <Plus className="h-4 w-4" /> Nueva página
        </button>
      </div>
      {isLoading ? <p className="cal-ayuda">Cargando…</p> : !hayPaginas ? (
        <div className="cal-citas-bienvenida" data-testid="cal-citas-vacio">
          <CalendarCheck className="h-9 w-9" style={{ color: 'var(--accent)' }} />
          <div>
            <h3>Que te reserven sin ir y venir correos</h3>
            <ol>
              <li><b>Crea una página</b> con la duración de la cita y tus horas disponibles.</li>
              <li><b>Comparte el enlace</b> por correo, WhatsApp o en tu firma.</li>
              <li><b>Quien reserva elige la hora</b>: la cita cae en tu calendario y le llega la confirmación.</li>
            </ol>
            <button type="button" className="od-btn-primary" onClick={() => abrirEditor(null)}><Plus className="h-4 w-4" /> Crear mi primera página</button>
          </div>
        </div>
      ) : (
        <div className="cal-paginas">
          {paginas.map((p) => (
            <TarjetaPagina
              key={p.id}
              p={p}
              tz={tz}
              hoy={hoy}
              onEditar={() => abrirEditor(p)}
              onDuplicar={() => duplicar(p)}
              onAlternar={() => alternar(p)}
              onBorrar={() => borrar(p)}
              onCopiar={() => copiar(p.url)}
              onEnviar={() => enviarEnlace(p)}
              onVerCitas={() => { setFiltroPagina(p.id); setPestana('proximas'); }}
            />
          ))}
        </div>
      )}

      {(hayPaginas || (proximas || []).length > 0) && (
        <>
          <div className="cal-citas-seccion-cab cal-citas-seccion-cab--lista">
            <div className="cal-segmentos" role="tablist" aria-label="Citas">
              {PESTANAS.map(([k, t]) => (
                <button key={k} type="button" role="tab" aria-selected={pestana === k} className={`cal-segmento ${pestana === k ? 'is-activa' : ''}`} onClick={() => setPestana(k)} data-testid={`cal-citas-pestana-${k}`}>
                  {t}{k === 'proximas' && proximas ? ` (${proximas.filter((c) => c.estado === 'confirmada').length})` : ''}
                </button>
              ))}
            </div>
            <span className="flex-1" />
            {(paginas || []).length > 1 && (
              <select className="cal-select cal-citas-filtro" value={filtroPagina} onChange={(e) => setFiltroPagina(e.target.value)} aria-label="Página de reserva">
                <option value="">Todas las páginas</option>
                {paginas.map((p) => <option key={p.id} value={p.id}>{p.titulo}</option>)}
              </select>
            )}
            <label className="cal-citas-buscar">
              <Search className="h-4 w-4" aria-hidden="true" />
              <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar persona" aria-label="Buscar por nombre, correo o teléfono" />
            </label>
          </div>

          {!grupos ? <p className="cal-ayuda">Cargando…</p> : grupos.length === 0 ? (
            <div className="cal-citas-nada">
              <CalendarX2 className="h-6 w-6" />
              <p>{busqueda || filtroPagina ? 'Ninguna cita coincide con el filtro.'
                : pestana === 'proximas' ? 'No tienes citas próximas. Comparte el enlace de una página para recibir reservas.'
                  : pestana === 'pasadas' ? 'Aún no hay citas pasadas.' : 'No hay citas canceladas.'}</p>
              {(busqueda || filtroPagina) && <button type="button" className="cal-mini-enlace" onClick={() => { setBusqueda(''); setFiltroPagina(''); }}>Quitar filtros</button>}
            </div>
          ) : (
            <div className="cal-citas-dias" data-testid="cal-lista-citas">
              {grupos.map((g) => (
                <section key={g.dia} className="cal-citas-dia">
                  <h4 className={g.dia === hoy ? 'is-hoy' : ''}>
                    {etiquetaDia(g.dia, hoy)}
                    {['Hoy', 'Mañana', 'Ayer'].includes(etiquetaDia(g.dia, hoy)) && <span> · {textoDiaLargo(g.dia)}</span>}
                    <span className="cal-contador">{g.citas.length}</span>
                  </h4>
                  <ul className="cal-lista-citas">
                    {g.citas.map((c) => (
                      <FilaCita
                        key={c.id}
                        c={c}
                        pagina={porPagina[c.pagina_id]}
                        tz={tz}
                        onAbrirEvento={onAbrirEvento}
                        onEscribir={() => escribir({ to: [c.correo], subject: c.pagina ? `Tu cita: ${c.pagina}` : 'Tu cita' })}
                        onCopiar={() => copiar(c.correo, 'Correo copiado')}
                        onCancelar={() => setCancelando(c)}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {editor && (
        <EditorPagina
          abierto
          pagina={editor.pagina}
          inicial={editor.inicial}
          calendarios={editables}
          propios={propios}
          onCerrar={() => setEditor(null)}
          onGuardar={guardar}
        />
      )}
      {cancelando && <DialogoCancelar cita={cancelando} tz={tz} onCerrar={() => setCancelando(null)} onConfirmar={cancelar} />}
    </div>
  );
}

function Dato({ icono, valor, texto, chico }) {
  return (
    <div className={`cal-dato ${chico ? 'cal-dato--chico' : ''}`}>
      <span className="cal-dato-ico" aria-hidden="true">{icono}</span>
      <div className="min-w-0">
        <div className="cal-dato-valor">{valor}</div>
        <div className="cal-dato-texto" title={typeof texto === 'string' ? texto : undefined}>{texto}</div>
      </div>
    </div>
  );
}

function TarjetaPagina({ p, tz, hoy, onEditar, onDuplicar, onAlternar, onBorrar, onCopiar, onEnviar, onVerCitas }) {
  const lugar = p.videollamada ? 'Orvian Meet' : p.ubicacion ? (esUrl(p.ubicacion) ? 'Videollamada' : p.ubicacion) : null;
  return (
    <article className={`cal-pag-tarjeta ${p.activa ? '' : 'is-pausada'}`} style={{ '--ev': p.color }} data-testid="cal-pagina">
      <div className="cal-pag-tarjeta-cab">
        <h3 className="truncate" title={p.titulo}>{p.titulo}</h3>
        <Switch checked={!!p.activa} onCheckedChange={onAlternar} aria-label={p.activa ? 'Pausar la página' : 'Activar la página'} title={p.activa ? 'Admite reservas' : 'Pausada'} data-testid="cal-pagina-activa" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="cal-icono-btn" aria-label={`Opciones de ${p.titulo}`}><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={onEditar}><Pencil className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
            <DropdownMenuItem onSelect={onDuplicar}><CopyPlus className="h-4 w-4 mr-2" />Duplicar</DropdownMenuItem>
            <DropdownMenuItem onSelect={onEnviar}><Send className="h-4 w-4 mr-2" />Enviar el enlace por correo</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onBorrar} style={{ color: 'var(--danger)' }}><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <p className="cal-pag-tarjeta-meta">
        <Clock className="h-3.5 w-3.5 shrink-0" />{duracionTexto(p.duracion)}
        {lugar && <> · {p.videollamada || esUrl(p.ubicacion) ? <Video className="h-3.5 w-3.5 shrink-0" /> : <MapPin className="h-3.5 w-3.5 shrink-0" />}<span className="truncate">{lugar}</span></>}
      </p>
      <p className="cal-pag-tarjeta-meta" title={resumenHorario(p.horario)}>
        <CalendarDays className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{resumenHorario(p.horario)}</span>
      </p>
      <button type="button" className="cal-pag-proximas" onClick={onVerCitas} disabled={!p.proximas}>
        {p.proximas ? (
          <>
            <b>{p.proximas}</b> {p.proximas === 1 ? 'cita próxima' : 'citas próximas'}
            {p.siguiente && <span> · la siguiente {etiquetaDia(ymdDe(p.siguiente, tz), hoy).toLowerCase()}, {fmtHora(p.siguiente, tz)}</span>}
          </>
        ) : p.activa ? 'Sin citas próximas' : 'Pausada: no admite reservas'}
      </button>
      <p className="cal-pag-tarjeta-url truncate" title={p.url}>{p.url.replace(/^https?:\/\//, '')}</p>
      <div className="cal-pag-tarjeta-acciones">
        <button type="button" className="od-btn-secondary" onClick={onCopiar} data-testid="cal-pagina-copiar"><Copy className="h-4 w-4" /> Copiar enlace</button>
        <a className="od-btn-secondary" href={p.url} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-4 w-4" /> Ver página</a>
      </div>
    </article>
  );
}

function FilaCita({ c, pagina, tz, onAbrirEvento, onEscribir, onCopiar, onCancelar }) {
  const ahora = Date.now();
  const ini = new Date(c.inicio).getTime();
  const fin = new Date(c.fin).getTime();
  const cancelada = c.estado === 'cancelada';
  const futura = fin > ahora;
  const enCurso = !cancelada && ini <= ahora && fin > ahora;
  const pronto = !cancelada && ini > ahora && ini - ahora <= 60 * 60000;
  const respuestas = Object.entries(c.respuestas || {}).filter(([, v]) => v);
  const min = Math.round((fin - ini) / 60000);
  return (
    <li className={`cal-cita ${cancelada ? 'is-cancelada' : ''} ${enCurso ? 'is-en-curso' : ''}`} style={{ '--ev': c.color || pagina?.color || 'var(--accent)' }} data-testid="cal-cita">
      <div className="cal-cita-cuando">
        <span className="cal-cita-hora">{fmtHora(c.inicio, tz)}</span>
        <span className="cal-cita-dur">{enCurso ? 'Ahora' : duracionTexto(min)}</span>
      </div>
      <span className="cal-cita-avatar" aria-hidden="true">{initials(c.nombre, c.correo)}</span>
      <div className="min-w-0 flex-1">
        <div className="cal-cita-nombre">
          <span className="truncate">{c.nombre}</span>
          {cancelada && <span className="cal-etiqueta-cancelada">{c.cancelada_por === 'anfitrion' ? 'La cancelaste' : 'La canceló'}</span>}
          {!cancelada && c.reprogramada_en && <span className="cal-etiqueta-cambio">Cambió de hora</span>}
        </div>
        <div className="cal-detalle-sub truncate">{c.pagina || 'Página eliminada'}</div>
        <div className="cal-cita-datos">
          <a href={`mailto:${c.correo}`} className="cal-enlace"><Mail className="h-3.5 w-3.5" />{c.correo}</a>
          {c.telefono && <a href={`tel:${c.telefono}`} className="cal-enlace"><Phone className="h-3.5 w-3.5" />{c.telefono}</a>}
        </div>
        {cancelada && c.motivo && <p className="cal-cita-motivo">«{c.motivo}»</p>}
        {(respuestas.length > 0 || c.nota) && (
          <dl className="cal-cita-respuestas">
            {respuestas.map(([k, v]) => {
              const q = (pagina?.preguntas || []).find((x) => x.id === k);
              return <React.Fragment key={k}><dt>{q?.etiqueta || 'Respuesta'}</dt><dd>{v}</dd></React.Fragment>;
            })}
            {c.nota && <><dt>Nota</dt><dd>{c.nota}</dd></>}
          </dl>
        )}
      </div>
      <div className="cal-cita-acciones">
        {c.meet_url && (enCurso || pronto) && (
          <a className="od-btn-primary cal-cita-unirse" href={c.meet_url} target="_blank" rel="noopener noreferrer"><Video className="h-4 w-4" /> Unirse</a>
        )}
        {!cancelada && c.evento_id && (
          <button type="button" className="od-btn-secondary" onClick={() => onAbrirEvento(c.evento_id)}>Ver en el calendario</button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="cal-icono-btn" aria-label={`Opciones de la cita con ${c.nombre}`} data-testid="cal-cita-menu"><MoreVertical className="h-4 w-4" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onSelect={onEscribir}><Mail className="h-4 w-4 mr-2" />Escribirle un correo</DropdownMenuItem>
            <DropdownMenuItem onSelect={onCopiar}><Copy className="h-4 w-4 mr-2" />Copiar su correo</DropdownMenuItem>
            {c.telefono && <DropdownMenuItem asChild><a href={`tel:${c.telefono}`}><Phone className="h-4 w-4 mr-2" />Llamar</a></DropdownMenuItem>}
            {c.meet_url && <DropdownMenuItem asChild><a href={c.meet_url} target="_blank" rel="noopener noreferrer"><Video className="h-4 w-4 mr-2" />Abrir la videollamada</a></DropdownMenuItem>}
            {!cancelada && futura && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={onCancelar} style={{ color: 'var(--danger)' }} data-testid="cal-cita-cancelar"><XCircle className="h-4 w-4 mr-2" />Cancelar la cita…</DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

function DialogoCancelar({ cita, tz, onCerrar, onConfirmar }) {
  const [motivo, setMotivo] = useState('');
  const [avisar, setAvisar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const confirmar = async () => {
    setEnviando(true);
    setError('');
    try { await onConfirmar({ motivo: motivo.trim(), avisar }); } catch (e) { setError(e.message); } finally { setEnviando(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v && !enviando) onCerrar(); }}>
      <DialogContent className="sm:max-w-[460px]" data-testid="cal-dialogo-cancelar-cita">
        <DialogHeader>
          <DialogTitle>¿Cancelar la cita con {cita.nombre}?</DialogTitle>
          <DialogDescription>
            {capital(textoDiaLargo(ymdDe(cita.inicio, tz)))}, {fmtHora(cita.inicio, tz)} – {fmtHora(cita.fin, tz)}{cita.pagina ? ` · ${cita.pagina}` : ''}.
            Se quitará de tu calendario{cita.meet_url ? ' y se cerrará su videollamada' : ''}.
          </DialogDescription>
        </DialogHeader>
        {error && <div className="cal-error" role="alert">{error}</div>}
        <label className="cal-etiqueta">Motivo (opcional)
          <textarea className="cal-input" rows={3} maxLength={1000} value={motivo} onChange={(e) => setMotivo(e.target.value)}
            placeholder="P. ej. Me surgió un imprevisto. ¿Podemos verlo otro día?" autoFocus data-testid="cal-cancelar-motivo" />
        </label>
        <label className="cal-check">
          <input type="checkbox" checked={avisar} onChange={(e) => setAvisar(e.target.checked)} />
          Avisarle por correo ({cita.correo}){motivo.trim() ? ', con el motivo' : ''} y ofrecerle reservar otra hora
        </label>
        <DialogFooter>
          <Button variant="outline" onClick={onCerrar} disabled={enviando}>No cancelar</Button>
          <Button variant="destructive" onClick={confirmar} disabled={enviando} data-testid="cal-cancelar-confirmar">{enviando ? 'Cancelando…' : 'Cancelar la cita'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
